/**
 * Play Along cost guards. Ungraded play needs no input, so a session can run
 * for hours, or be left running in a tab nobody is looking at. Every phrase of
 * sight reading is fetched from the backend, so these keep that bounded:
 * - a pause after 30 minutes with no interaction at all (makeIdleTimer);
 * - no phrase request while the page is hidden (makeHiddenPageRequestGate);
 * - repeats avoided over the last 30 phrases shown rather than the whole
 *   session, so a long session never runs out of phrases (recentlyServedWith).
 */

/** How long ungraded play runs with no interaction before it pauses. */
export const PLAY_ALONG_IDLE_PAUSE_MS = 30 * 60 * 1000;

/** How many of the latest phrases shown ungraded play avoids repeating. */
export const PLAY_ALONG_NO_REPEAT_WINDOW = 30;

/** Fired on the window when the player plays a note (see notePlayAlongActivity). */
export const PLAY_ALONG_ACTIVITY_EVENT = 'museflow_play_along_activity';

export interface IdleTimer {
  /** Start (or restart) the countdown. */
  arm: () => void;
  /** The player did something: restart the countdown if it is running. */
  touch: () => void;
  /** Stop the countdown. */
  disarm: () => void;
}

export interface IdleTimerOptions {
  timeoutMs: number;
  onIdle: () => void;
}

/**
 * A countdown restarted by every interaction. It fires once, when the time
 * passes with none, and does not restart by itself.
 */
export function makeIdleTimer({ timeoutMs, onIdle }: IdleTimerOptions): IdleTimer {
  let pending: ReturnType<typeof setTimeout> | undefined;

  const disarm = () => {
    if (pending !== undefined) clearTimeout(pending);
    pending = undefined;
  };
  const arm = () => {
    disarm();
    pending = setTimeout(() => {
      pending = undefined;
      onIdle();
    }, timeoutMs);
  };
  const touch = () => {
    if (pending !== undefined) arm();
  };

  return { arm, touch, disarm };
}

/** Tell a running idle pause that the player played a note. */
export const notePlayAlongActivity = (): void => {
  window.dispatchEvent(new Event(PLAY_ALONG_ACTIVITY_EVENT));
};

export interface HiddenPageRequestGateOptions<Request> {
  /** True while requests are held: ungraded play on a hidden page. */
  shouldHold: () => boolean;
  /** Requests held so far. Kept by the caller so they outlive a re-registration. */
  held: { current: readonly Request[] };
  /** Sends a request on. */
  forward: (request: Request) => void;
}

export interface HiddenPageRequestGate<Request> {
  /** Send the request on, or hold it while the page is hidden. */
  request: (request: Request) => void;
  /** Send the held requests on once the page is back (call on visibilitychange). */
  release: () => void;
}

/**
 * Holds requests made while the page is hidden and sends them, in order, when
 * it is shown again. Requests are held rather than dropped: the scene waits for
 * an answer to each one.
 */
export function makeHiddenPageRequestGate<Request>({
  shouldHold,
  held,
  forward,
}: HiddenPageRequestGateOptions<Request>): HiddenPageRequestGate<Request> {
  return {
    request: (request) => {
      if (shouldHold()) {
        held.current = [...held.current, request];
        return;
      }
      forward(request);
    },
    release: () => {
      if (shouldHold() || held.current.length === 0) return;
      const toSend = held.current;
      held.current = [];
      toSend.forEach((request) => forward(request));
    },
  };
}

/**
 * The phrase hashes to avoid after `hash` is shown: the latest `windowSize`
 * shown, `hash` last. Returns a new set; `served` is not changed.
 */
export const recentlyServedWith = (
  served: ReadonlySet<string>,
  hash: string,
  windowSize: number = PLAY_ALONG_NO_REPEAT_WINDOW,
): Set<string> =>
  new Set([...[...served].filter((servedHash) => servedHash !== hash), hash].slice(-windowSize));
